import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import MainLayout from '../../components/layout/main/main-layout';
import {
	fetchCompaniesPayments,
	updateCompaniesPayments,
} from '../../services/api/company/company';
import { formatNumber } from '../../utils/strings';
import { PAYMENTS_STATUS } from '../../utils/status';

const CompaniesPaymentsPage = () => {
	const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
	const [status, setStatus] = useState('pending');
	const [loading, setLoading] = useState(false);
	const [payments, setPayments] = useState([]);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [selectedPayments, setSelectedPayments] = useState(new Set());
	const business = useSelector(({ auth }) => auth.business);
	const companiesMap = useSelector(({ companies }) => companies.companiesMap);

	useEffect(() => {
		const fetchPayments = async () => {
			if (!business?.id || !date) return;
			try {
				setLoading(true);
				const response = await fetchCompaniesPayments(
					business.id,
					date,
					status || ''
				);
				setPayments(response ?? []);
				setSelectedCompany(null);
				setSelectedPayments(new Set());
			} catch (error) {
				console.error('Error fetching companies payments:', error);
			} finally {
				setLoading(false);
			}
		};
		fetchPayments();
	}, [business?.id, date, status]);

	// Group payments by company and status
	const groupedPayments = payments.reduce((acc, payment) => {
		const companyId = payment.company;
		const paymentStatus = payment.status || 'pending';
		
		if (!acc[companyId]) {
			acc[companyId] = {};
		}
		if (!acc[companyId][paymentStatus]) {
			acc[companyId][paymentStatus] = [];
		}
		acc[companyId][paymentStatus].push(payment);
		return acc;
	}, {});

	// Calculate total pending payments for a company
	const getPendingTotal = (companyId) => {
		const companyPayments = groupedPayments[companyId] || {};
		const pendingPayments = companyPayments.pending || [];
		return pendingPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
	};

	// Toggle selection of all pending payments for a company
	const toggleCompanySelection = (companyId) => {
		if (selectedCompany === companyId) {
			setSelectedCompany(null);
			setSelectedPayments(new Set());
		} else {
			setSelectedCompany(companyId);
			const companyPayments = groupedPayments[companyId] || {};
			const pendingPayments = companyPayments.pending || [];
			setSelectedPayments(new Set(pendingPayments.map(p => p.id)));
		}
	};

	// Toggle individual payment selection
	const togglePaymentSelection = (paymentId) => {
		const newSelected = new Set(selectedPayments);
		if (newSelected.has(paymentId)) {
			newSelected.delete(paymentId);
		} else {
			newSelected.add(paymentId);
		}
		setSelectedPayments(newSelected);
	};

	// Handle payment update
	const handlePay = async () => {
		if (selectedPayments.size === 0) return;

		const selectedPaymentIds = Array.from(selectedPayments);
		const selectedPaymentObjects = payments.filter(p => selectedPaymentIds.includes(p.id));
		const references = selectedPaymentObjects.map(p => p.reference).filter(Boolean);
		const details = `Pago de los pedidos ${references.join(', ')}`;

		const confirmed = window.confirm(
			`¿Estás seguro de marcar ${selectedPaymentIds.length} pago(s) como pagado(s)?\n\nReferencias: ${references.join(', ')}\nTotal: $${formatNumber(selectedPaymentObjects.reduce((sum, p) => sum + (p.amount || 0), 0))}`
		);

		if (!confirmed) return;

		try {
			setLoading(true);
			await updateCompaniesPayments(selectedPaymentIds, 'paid', details, '');
			
			// Refresh payments
			const response = await fetchCompaniesPayments(
				business.id,
				date,
				status || ''
			);
			setPayments(response ?? []);
			setSelectedCompany(null);
			setSelectedPayments(new Set());
		} catch (error) {
			console.error('Error updating companies payments:', error);
			alert('Error al actualizar los pagos. Por favor, intenta nuevamente.');
		} finally {
			setLoading(false);
		}
	};

	const renderPayment = (payment) => {
		const company = companiesMap[payment.company];
		const isSelected = selectedPayments.has(payment.id);
		const paymentStatus = PAYMENTS_STATUS[payment.status] || PAYMENTS_STATUS.pending;
		const createdDate = payment._created 
			? moment(payment._created).format('DD/MM/YYYY HH:mm')
			: '';
		const dueDate = payment.dueDate 
			? moment(payment.dueDate).format('DD/MM/YYYY HH:mm')
			: '';
		const paymentDate = payment.paymentDate 
			? moment(payment.paymentDate).format('DD/MM/YYYY HH:mm')
			: '';

		return (
			<div
				key={payment.id}
				className={`flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg mb-2 transition-all ${
					isSelected 
						? 'border-blue-500 bg-blue-50' 
						: 'border-gray-200 bg-white hover:border-gray-300'
				}`}
			>
				<div className="flex-1 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
					{selectedCompany === payment.company && payment.status === 'pending' && (
						<input
							type="checkbox"
							checked={isSelected}
							onChange={() => togglePaymentSelection(payment.id)}
							className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
						/>
					)}
					<div className="flex-1">
						<div className="flex flex-wrap items-center gap-2 mb-2">
							<p className="font-bold text-[16px] text-gray-800">
								{company?.name || `Empresa ${payment.company}`}
							</p>
							<span
								className="px-2 py-1 rounded text-xs font-semibold"
								style={{
									backgroundColor: `${paymentStatus.color}20`,
									color: paymentStatus.color,
								}}
							>
								{paymentStatus.text}
							</span>
						</div>
						{dueDate && (
							<p className="text-[15px] text-orange-600 font-semibold mb-2">
								⏰ Vence: {dueDate}
							</p>
						)}
						{payment.reference && (
							<p className="text-[14px] text-gray-600 mb-1">
								Referencia: <span className="font-semibold">{payment.reference}</span>
							</p>
						)}
						{payment.order && (
							<p className="text-[14px] text-gray-600 mb-1">
								Pedido: <span className="font-semibold">{payment.reference}</span>
							</p>
						)}
						{createdDate && (
							<p className="text-[12px] text-gray-500">
								Creado: {createdDate}
							</p>
						)}
						{payment.status === 'paid' && paymentDate && (
							<p className="text-[12px] text-green-600 font-semibold">
								Pagado: {paymentDate}
							</p>
						)}
					</div>
					<div className="flex flex-col items-end">
						<p className="text-[20px] font-bold text-green-600">
							${formatNumber(payment.amount || 0)}
						</p>
					</div>
				</div>
			</div>
		);
	};

	const renderCompanyGroup = (companyId) => {
		const company = companiesMap[companyId];
		const companyPayments = groupedPayments[companyId] || {};
		const pendingPayments = companyPayments.pending || [];
		const paidPayments = companyPayments.paid || [];
		const allPayments = [...pendingPayments, ...paidPayments];
		const pendingTotal = getPendingTotal(companyId);
		const isSelected = selectedCompany === companyId;
		const hasPendingPayments = pendingPayments.length > 0;

		// Filter payments based on status filter
		const filteredPayments = allPayments.filter(payment => {
			if (status === '') return true;
			return payment.status === status;
		});

		if (filteredPayments.length === 0) return null;

		return (
			<div key={companyId} className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
				<div className="flex flex-col md:flex-row md:items-center justify-between mb-4 pb-3 border-b border-gray-300">
					<div className="flex-1">
						<h3 className="text-xl font-bold text-gray-800 mb-1">
							{company?.name || `Empresa ${companyId}`}
						</h3>
						<div className="flex flex-wrap gap-2 text-sm text-gray-600">
							<span>Pendientes: {pendingPayments.length}</span>
							<span>•</span>
							<span>Pagados: {paidPayments.length}</span>
							{hasPendingPayments && (
								<>
									<span>•</span>
									<span className="font-bold text-green-600">
										Total pendiente: ${formatNumber(pendingTotal)}
									</span>
								</>
							)}
						</div>
					</div>
					{hasPendingPayments && (status === 'pending' || status === '') && (
						<button
							type="button"
							onClick={() => toggleCompanySelection(companyId)}
							className={`px-4 py-2 rounded font-medium transition-colors mt-2 md:mt-0 ${
								isSelected
									? 'bg-blue-600 text-white hover:bg-blue-700'
									: 'bg-gray-200 text-gray-700 hover:bg-gray-300'
							}`}
						>
							{isSelected ? 'Deseleccionar' : 'Seleccionar pendientes'}
						</button>
					)}
				</div>
				<div className="flex flex-col gap-2">
					{filteredPayments.map((payment) => renderPayment(payment))}
				</div>
			</div>
		);
	};

	return (
		<MainLayout>
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-800 mb-6">
					Pagos de Empresas
				</h1>
				
				<div className="flex flex-wrap items-end gap-4 mb-6">
					<div>
						<label
							className="block text-sm font-medium text-gray-700 mb-1"
							htmlFor="date"
						>
							Fecha
						</label>
						<input
							id="date"
							type="date"
							className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
							value={date || ''}
							onChange={(e) => setDate(e.target.value)}
							max={moment().format('YYYY-MM-DD')}
						/>
					</div>
					<div>
						<label
							className="block text-sm font-medium text-gray-700 mb-1"
							htmlFor="status"
						>
							Estado
						</label>
						<select
							id="status"
							className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
							value={status}
							onChange={(e) => setStatus(e.target.value)}
						>
							<option value="pending">Pendiente</option>
							<option value="paid">Pagado</option>
							<option value="">Todos</option>
						</select>
					</div>
					<button
						type="button"
						className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-medium"
						disabled={loading}
						onClick={() => {
							setDate(moment().format('YYYY-MM-DD'));
							setStatus('pending');
						}}
					>
						Limpiar
					</button>
					{selectedPayments.size > 0 && (
						<button
							type="button"
							className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium shadow-md"
							disabled={loading}
							onClick={handlePay}
						>
							Pagar ({selectedPayments.size})
						</button>
					)}
				</div>

				{loading && (
					<div className="text-center py-8">
						<p className="text-gray-500">Cargando pagos...</p>
					</div>
				)}

				{!loading && payments.length === 0 && (
					<div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
						<p className="text-gray-500 text-lg">
							No se encontraron pagos para los filtros seleccionados.
						</p>
					</div>
				)}

				{!loading && payments.length > 0 && (
					<div className="flex flex-col gap-4">
						{Object.keys(groupedPayments)
							.sort((a, b) => {
								const nameA = companiesMap[a]?.name || '';
								const nameB = companiesMap[b]?.name || '';
								return nameA.localeCompare(nameB);
							})
							.map((companyId) => renderCompanyGroup(companyId))}
					</div>
				)}
			</div>
		</MainLayout>
	);
};

export default CompaniesPaymentsPage;
